import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GetAllClientsAction,
  AddClientAction,
  GetClientByIdAction,
  UpdateClientStatusAction
} from "../../actions/ClientAction";
// Import selectors from reducer
import {
  selectAllClientsData,
  selectAllClientsLoading,
  selectAllClientsError,
  selectAddClientData,
  selectAddClientLoading,
  selectAddClientError,
  selectClientByIdData,
  selectClientByIdLoading,
  selectClientByIdError,
  selectUpdateClientStatusData, 
  selectUpdateClientStatusLoading,
  selectUpdateClientStatusError
} from "../../reducers/ClientReducer";

// Import components
import ClientPagination from '../../components/common/ClientPagination';
import AddClientModal from '../../components/modals/AddClientModal';
import ClientDetailsModal from '../../components/modals/ClientDetailsModal';
import ClientsTable from '../../components/tables/ClientsTable';

const ClientManagement = () => {
  const dispatch = useDispatch();

   // Use selectors from reducer
  const clientsData = useSelector(selectAllClientsData);
  const clientsLoading = useSelector(selectAllClientsLoading);
  const clientsError = useSelector(selectAllClientsError);

  const addClientResult = useSelector(selectAddClientData);
  const addClientLoading = useSelector(selectAddClientLoading);
  const addClientError = useSelector(selectAddClientError);

  const clientByIdResult = useSelector(selectClientByIdData);
  const clientByIdLoading = useSelector(selectClientByIdLoading);
  const clientByIdError = useSelector(selectClientByIdError);

  const updateClientStatusResult = useSelector(selectUpdateClientStatusData);
  const updateClientStatusLoading = useSelector(selectUpdateClientStatusLoading);
  const updateClientStatusError = useSelector(selectUpdateClientStatusError);

  const [clients, setClients] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedStatus, setSelectedStatus] = useState({});

  const [newClient, setNewClient] = useState({
    client_name: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    country: '',
    status: 'Active'
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const formatClientStatus = (status) => {
    if (status === "A" || status === "Active" || status === "active") {
      return "Active";
    } else if (status === "I" || status === "Inactive" || status === "inactive") {
      return "Inactive";
    } else {
      return "Active";
    }
  };

  const getApiStatusCode = (displayStatus) => {
    switch (displayStatus) {
      case "Active": return "A";
      case "Inactive": return "I";
      default: return "A";
    }
  };

  const getStatusBadgeColor = (status) => {
    const formattedStatus = formatClientStatus(status);
    switch (formattedStatus) {
      case 'Active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Inactive':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'client_name':
      case 'contact_person':
      case 'city':
      case 'country':
        if (!value.trim()) {
          return 'This field is required';
        }
        if (!/.*[a-zA-Z].*/.test(value) || value.length < 2) {
          return 'Must contain letters and be at least 2 characters';
        }
        if (/^\d+$/.test(value)) {
          return 'Cannot be numbers only';
        }
        if (/^[^a-zA-Z0-9]*$/.test(value)) {
          return 'Must contain valid characters';
        }
        return '';

      case 'contact_phone':
        if (!value.trim()) {
          return 'This field is required';
        }
        if (!/^\d{10}$/.test(value)) {
          return 'Must be exactly 10 digits';
        }
        return '';

      case 'contact_email':
        if (!value.trim()) {
          return 'This field is required';
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Invalid email format';
        }
        return '';

      case 'address_line1':
        if (!value.trim()) {
          return 'This field is required';
        }
        if (value.length < 5) {
          return 'Address must be at least 5 characters';
        }
        return '';

      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ['client_name', 'contact_person', 'contact_phone', 'contact_email', 'address_line1', 'city', 'country'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, newClient[field]);
      if (error) errors[field] = error;
    });

    const duplicateEmail = clients.some(client => 
      client.contact_email.toLowerCase() === newClient.contact_email.toLowerCase()
    );
    if (duplicateEmail) {
      errors.contact_email = 'Email already exists';
    }

    const duplicatePhone = clients.some(client => 
      client.contact_phone === newClient.contact_phone
    );
    if (duplicatePhone) {
      errors.contact_phone = 'Phone number already exists';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatAndSetClients = useCallback((data) => {
    let clientsArray = [];

    if (Array.isArray(data)) {
      clientsArray = data;
    } else if (data && typeof data === 'object') {
      if (Array.isArray(data.ResultSet)) clientsArray = data.ResultSet;
      else if (Array.isArray(data.resultSet)) clientsArray = data.resultSet;
      else if (Array.isArray(data.data)) clientsArray = data.data;
      else if (Array.isArray(data.responseBody)) clientsArray = data.responseBody;
      else if (Array.isArray(data.clients)) clientsArray = data.clients;
      else if (data.client_id) clientsArray = [data];
    }

    const formatted = clientsArray.map((client, index) => ({
      client_id: client.client_id || client.ClientID || `temp-${index}`,
      client_name: client.client_name || client.ClientName || client.name || 'Unnamed',
      contact_person: client.contact_person || client.ContactPerson || 'No contact',
      contact_phone: client.contact_phone || client.ContactPhone || client.phone || 'No phone',
      contact_email: client.contact_email || client.ContactEmail || client.email || 'No email',
      address_line1: client.address_line1 || client.AddressLine1 || client.address1 || 'No address',
      address_line2: client.address_line2 || client.AddressLine2 || client.address2 || '',
      city: client.city || client.City || 'No city',
      country: client.country || client.Country || 'No country',
      business_name: client.linked_business_name || client.business_name || client.BusinessName || client.company_name || 'N/A',
      industry_type: client.linked_industry_type || client.industry_type || client.IndustryType || client.industry || 'N/A',
      status: client.status || client.Status || "A"
    }));

    setClients(formatted);
    
    const initialStatus = {};
    formatted.forEach(client => {
      initialStatus[client.client_id] = formatClientStatus(client.status);
    });
    setSelectedStatus(initialStatus);
    
    setTotalPages(Math.ceil(formatted.length / itemsPerPage));
  }, [itemsPerPage]);

  const loadClients = useCallback(async () => {
    try {
      dispatch(GetAllClientsAction());
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    if (clientsData) {
      formatAndSetClients(clientsData);
    }
  }, [clientsData, formatAndSetClients]);

  useEffect(() => {
    if (clientByIdResult && !clientByIdLoading) {
      formatAndSetClients(clientByIdResult);
      setCurrentPage(1);
    }
  }, [clientByIdResult, clientByIdLoading, formatAndSetClients]);

  useEffect(() => {
    if (addClientResult && !addClientLoading) {
      console.log('New client added successfully, reloading clients...');
      loadClients();
      setShowAddForm(false);
      setNewClient({
        client_name: '',
        contact_person: '',
        contact_phone: '',
        contact_email: '',
        address_line1: '',
        address_line2: '',
        city: '',
        country: '',
        status: 'Active'
      });
      setValidationErrors({});
      setTouchedFields({});
    }
  }, [addClientResult, addClientLoading, loadClients]);

  useEffect(() => {
    if (updateClientStatusResult && !updateClientStatusLoading) {
      console.log('Client status updated successfully');
      loadClients();
    }
  }, [updateClientStatusResult, updateClientStatusLoading, loadClients]);

  const handleAddClient = async (e) => {
    e.preventDefault();

    const allFields = ['client_name', 'contact_person', 'contact_phone', 'contact_email', 'address_line1', 'city', 'country'];
    const touched = {};
    allFields.forEach(field => {
      touched[field] = true;
    });
    setTouchedFields(touched);

    if (!validateForm()) {
      return;
    }

    try {
      const apiStatus = getApiStatusCode(newClient.status);
      const clientDataWithApiStatus = {
        ...newClient,
        status: apiStatus
      };
      dispatch(AddClientAction(clientDataWithApiStatus));
    } catch (error) {
      console.error('Error adding client:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      await loadClients();
      return;
    }

    try {
      dispatch(GetClientByIdAction(searchTerm));
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    if (['client_name', 'contact_person', 'city', 'country', 'address_line1'].includes(name)) {
      const filteredValue = value.replace(/[^a-zA-Z0-9\s\-.,]/g, '');
      setNewClient(prev => ({ 
        ...prev, 
        [name]: filteredValue 
      }));
      
      const error = validateField(name, filteredValue);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    } else if (name === 'contact_phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setNewClient(prev => ({ 
        ...prev, 
        [name]: numericValue 
      }));
      
      const error = validateField(name, numericValue);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    } else {
      setNewClient(prev => ({ 
        ...prev, 
        [name]: value 
      }));
      
      const error = validateField(name, value);
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleStatusChange = async (clientId, newDisplayStatus) => {
    if (!clientId || !newDisplayStatus) {
      alert('Client ID and Status are required');
      return;
    }

    try {
      const apiStatus = getApiStatusCode(newDisplayStatus);
      
      setSelectedStatus(prev => ({
        ...prev,
        [clientId]: newDisplayStatus
      }));

      await dispatch(UpdateClientStatusAction(clientId, apiStatus));
      
    } catch (error) {
      console.error('Error updating client status:', error);
      loadClients();
    }
  };

  const handleViewClick = async (client) => {
    try {
      setSelectedClient(client);
      setShowViewModal(true);
    } catch (error) {
      console.error('Error loading client details:', error);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    loadClients();
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setNewClient({
      client_name: '',
      contact_person: '',
      contact_phone: '',
      contact_email: '',
      address_line1: '',
      address_line2: '',
      city: '',
      country: '',
      status: 'Active'
    });
    setValidationErrors({});
    setTouchedFields({});
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClients = clients.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">Client Management</h1>
        <p className="text-sm md:text-base text-gray-600">Manage your client information and contacts</p>
      </div>

      {(clientsError || addClientError || clientByIdError || updateClientStatusError) && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm md:text-base">
          <strong>Error:</strong> {clientsError || addClientError || clientByIdError || updateClientStatusError}
        </div>
      )}

      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <form onSubmit={handleSearch} className="flex-1 max-w-full lg:max-w-md relative">
          <input
            type="text"
            placeholder="Search clients by ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={clientsLoading || clientByIdLoading || updateClientStatusLoading}
          />
          <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
          {searchTerm && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              disabled={clientsLoading || clientByIdLoading || updateClientStatusLoading}
            >
              <X size={20} />
            </button>
          )}
        </form>
        
        <button 
          onClick={() => setShowAddForm(true)}
          disabled={addClientLoading || clientsLoading || updateClientStatusLoading}
          className="bg-blue-500 text-white px-4 md:px-6 py-2 rounded-lg flex items-center justify-center space-x-2 lg:ml-4 hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed w-full lg:w-auto"
        >
          <Plus size={20} />
          <span className="text-sm md:text-base">{addClientLoading ? 'Adding...' : 'Add New Client'}</span>
        </button>
      </div>

      {/* Use AddClientModal component */}
      <AddClientModal
        showAddForm={showAddForm}
        newClient={newClient}
        handleInputChange={handleInputChange}
        validationErrors={validationErrors}
        touchedFields={touchedFields}
        setTouchedFields={setTouchedFields}
        addClientLoading={addClientLoading}
        addClientError={addClientError}
        handleAddClient={handleAddClient}
        handleCloseAddForm={handleCloseAddForm}
      />

      {(clientsLoading || clientByIdLoading || updateClientStatusLoading) && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600 text-sm md:text-base">
            {clientByIdLoading ? 'Searching clients...' : 
             updateClientStatusLoading ? 'Updating status...' : 'Loading clients...'}
          </p>
        </div>
      )}

      {/* Clients Table Container */}
      {!clientsLoading && !clientByIdLoading && !updateClientStatusLoading && (
        <div className="flex-1 min-h-0 overflow-x-auto">
          <ClientsTable
            currentClients={currentClients}
            searchTerm={searchTerm}
            handleViewClick={handleViewClick}
            updateClientStatusLoading={updateClientStatusLoading}
            selectedStatus={selectedStatus}
            handleStatusChange={handleStatusChange}
            getStatusBadgeColor={getStatusBadgeColor}
            formatClientStatus={formatClientStatus}
          />
        </div>
      )}

      {/* Use ClientPagination component */}
      {clients.length > 0 && (
        <div className="flex-shrink-0 mt-4">
          <ClientPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={clients.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
          />
        </div>
      )}

      {/* Use ClientDetailsModal component */}
      <ClientDetailsModal
        selectedClient={selectedClient}
        showViewModal={showViewModal}
        setShowViewModal={setShowViewModal}
        getStatusBadgeColor={getStatusBadgeColor}
        formatClientStatus={formatClientStatus}
      />
    </div>
  );
};

export default ClientManagement;